import config from "../resources/service_config.json";
import { runtimeEnv } from "./runtimeEnv";
import {
  getTokenRoleSnapshot,
  resolveConnectorServiceForSnapshot,
} from "./auth/connectorRouting";
import {
  getConfiguredAdminPort,
  getResolvedAdminConnectorUrl,
  getResolvedAdminConnectorHost,
  getResolvedAdminWorkerPort,
  getStoredAdminConnectorConfig,
} from "./auth/adminConnectorRuntime";

const envKey = runtimeEnv.REACT_APP_ENV;

if (!config[envKey]) {
  throw new Error(
    `Configuration file for '${envKey}' no found in service_config.json.`
  );
}

const currentConfig = config[envKey];
const RUNTIME_PORT_EXCLUDED_SERVICES = new Set([
  "platformFrontend",
  "keycloak",
]);

function getEnvironmentBaseUrl() {
  let baseURL = "";

  if (envKey === "development") {
    baseURL = runtimeEnv.REACT_APP_URI_DEVELOP;
  } else if (envKey === "test") {
    baseURL = runtimeEnv.REACT_APP_URI_TEST;
  } else if (envKey === "pro") {
    baseURL = runtimeEnv.REACT_APP_URI_PRO || runtimeEnv.REACT_APP_URI_PROD;
  } else if (envKey === "pre" || envKey === "preproduction") {
    baseURL =
      runtimeEnv.REACT_APP_URI_PRE ||
      runtimeEnv.REACT_APP_URI_PREPRODUCTION ||
      runtimeEnv.REACT_APP_URI_PRO ||
      runtimeEnv.REACT_APP_URI_PROD;
  } else if (envKey === "production") {
    baseURL = runtimeEnv.REACT_APP_URI_PROD;
  } else if (envKey === "local") {
    baseURL = runtimeEnv.REACT_APP_URI_LOCAL;
  }

  return baseURL;
}

function trimTrailingSlashes(value) {
  return String(value || "").replace(/\/+$/, "");
}

function normalizePathPrefix(value) {
  const path = String(value || "").trim();

  if (!path || path === "/") {
    return "";
  }

  return path.startsWith("/") ? path : `/${path}`;
}

function joinBaseUrlAndPath(baseURL, pathPrefix) {
  const normalizedBaseUrl = trimTrailingSlashes(baseURL);
  const normalizedPathPrefix = normalizePathPrefix(pathPrefix);

  if (!normalizedBaseUrl) {
    return normalizedPathPrefix;
  }

  return `${normalizedBaseUrl}${normalizedPathPrefix}`;
}

function buildBaseUrlWithRuntime(baseURL, runtimeConfig = {}) {
  const { port, host } = runtimeConfig;
  const normalizedPort = String(port ?? "")
    .trim()
    .replace(/^:/, "");

  if (!baseURL || (!normalizedPort && !host)) {
    return baseURL;
  }

  const urlMatch = baseURL.match(/^([a-z]+:\/\/)([^/]+)(.*)$/i);

  if (!urlMatch) {
    return baseURL.replace(/\/$/, "");
  }

  const [, protocol, authority, suffix] = urlMatch;
  let currentHost = authority;
  let currentPort = "";

  if (authority.startsWith("[")) {
    const ipv6ClosingIndex = authority.indexOf("]");

    if (ipv6ClosingIndex !== -1) {
      currentHost = authority.slice(0, ipv6ClosingIndex + 1);
      currentPort =
        authority[ipv6ClosingIndex + 1] === ":"
          ? authority.slice(ipv6ClosingIndex + 2)
          : "";
    }
  } else {
    const lastColonIndex = authority.lastIndexOf(":");

    if (lastColonIndex !== -1 && /^\d+$/.test(authority.slice(lastColonIndex + 1))) {
      currentHost = authority.slice(0, lastColonIndex);
      currentPort = authority.slice(lastColonIndex + 1);
    }
  }

  const resolvedHost = host || currentHost;
  const resolvedPort = normalizedPort || currentPort;

  return `${protocol}${resolvedHost}${resolvedPort ? `:${resolvedPort}` : ""}${suffix}`.replace(
    /\/$/,
    "",
  );
}

function getServicePathOrPort(serviceName) {
  const servicePathOrPort = currentConfig.services[serviceName];
  if (!servicePathOrPort) {
    throw new Error(
      `'${serviceName}' service not found for environment '${envKey}'.`
    );
  }

  return servicePathOrPort;
}

function getServicePathSuffix(serviceName) {
  const servicePathOrPort = getServicePathOrPort(serviceName);

  if (/^:\d+(\/.*)?$/.test(servicePathOrPort)) {
    return servicePathOrPort.replace(/^:\d+/, "");
  }

  return servicePathOrPort;
}

function getServiceBaseUrl(serviceName) {
  const servicePathOrPort = getServicePathOrPort(serviceName);

  if (/^:\d+(\/.*)?$/.test(servicePathOrPort)) {
    return `${trimTrailingSlashes(getEnvironmentBaseUrl())}${servicePathOrPort}`;
  }

  return joinBaseUrlAndPath(getEnvironmentBaseUrl(), servicePathOrPort);
}

function getRuntimeAwareBaseUrl(serviceName, runtimeConfig, resolvedServiceName) {
  if (runtimeConfig?.connectorUrl) {
    return trimTrailingSlashes(runtimeConfig.connectorUrl);
  }

  const baseURL = buildBaseUrlWithRuntime(getEnvironmentBaseUrl(), runtimeConfig);
  const servicePathSuffix = getServicePathSuffix(resolvedServiceName || serviceName);

  return joinBaseUrlAndPath(baseURL, servicePathSuffix);
}

function hasRuntimeAddress(config) {
  return Boolean(config?.port || config?.host || config?.connectorUrl);
}

function getRouteRoleForService(serviceName) {
  if (serviceName === "connectorWorkerConsumer") {
    return "data_consumer";
  }

  if (
    serviceName === "springConnector" ||
    serviceName === "connectorWorkerProvider"
  ) {
    return "data_provider";
  }

  return "";
}

function getRuntimeConfigForSnapshot(snapshot, serviceName) {
  const storedConfig = getStoredAdminConnectorConfig();
  const routeRole = getRouteRoleForService(serviceName);
  const roleRoute = routeRole ? storedConfig?.routes?.[routeRole] : null;

  if (hasRuntimeAddress(roleRoute)) {
    return {
      port: roleRoute.port,
      host: roleRoute.host,
      connectorUrl: roleRoute.connectorUrl,
    };
  }

  const storedPort = getResolvedAdminWorkerPort();
  const storedHost = getResolvedAdminConnectorHost();
  const storedConnectorUrl = getResolvedAdminConnectorUrl();

  if (storedPort || storedHost || storedConnectorUrl) {
    return {
      port: storedPort,
      host: storedHost,
      connectorUrl: storedConnectorUrl,
    };
  }

  if (snapshot?.isAdmin) {
    return {
      port: getConfiguredAdminPort(),
      host: "",
    };
  }

  return null;
}

function shouldUseRuntimePort(serviceName, options) {
  if (options?.useRuntimePort === false) {
    return false;
  }

  return !RUNTIME_PORT_EXCLUDED_SERVICES.has(serviceName);
}

export function buildServiceUrl(serviceName, endpointPath = "", options = {}) {
  const snapshot = getTokenRoleSnapshot(options?.token);
  const resolvedServiceName =
    options?.useRoleBasedConnectorRouting === false
      ? serviceName
      : resolveConnectorServiceForSnapshot(snapshot, serviceName);
  const runtimeConfig =
    shouldUseRuntimePort(serviceName, options) &&
    getRuntimeConfigForSnapshot(snapshot, resolvedServiceName);
  const baseUrl = runtimeConfig
    ? getRuntimeAwareBaseUrl(serviceName, runtimeConfig, resolvedServiceName)
    : getServiceBaseUrl(resolvedServiceName);

  const cleanEndpointPath = endpointPath.startsWith("/")
    ? endpointPath
    : `/${endpointPath}`;
  const finalUrl = `${baseUrl.replace(/\/$/, "")}${cleanEndpointPath}`;

  if (endpointPath === "") {
    return finalUrl.replace(/\/$/, "");
  }

  return finalUrl;
}
